import React, { FC } from 'react'
import { useSelector } from 'react-redux'
import { professionsIdSelectors } from '@ucheba/store/professions/id'
import styled from 'styled-components'
import { Container } from '@ucheba/ui/components/Container/desktop'
import { Heading } from '@ucheba/ui/components/Heading/desktop'
import { Spacing } from '@ucheba/ui/components/Spacing/desktop'
import { Grid, GridCol } from '@ucheba/ui/components/Grid/desktop'
import { Text } from '@ucheba/ui/components/Text/desktop'
import { institutionsSelectors } from '@ucheba/store/institutions'
import { specialitiesSearchSelectors } from '@ucheba/store/specialities/search'
import { useProfByIdCore } from '../bll/useProfByIdCore'
import { Banner } from '../../../../components/Banner/desktop'
import ProfCardStats from '../components/ProfCardStats/desktop/ProfCardStats'
import TextBlock from '../components/TextBlock/desktop/TextBlock'
import WhereAreStudying from '../components/WhereAreStudying/desktop/WhereAreStuding'
import SimilarProfBlock from '../components/SimilarProfBlock/desktop/SimilarProfBlock'
import SpecialitiesByProf from '../components/SpecialitiesByProf/desktop/SpecialitiesByProf'
import AboutCases from '../components/AboutCases/desktop'
import PopularPersons from '../components/PopularPersons/desktop/PopularPersons'
import AboutCompanies from '../components/AboutCompanies/desktop/AboutCompanies'
import { IProfIdPageProps } from '../types'

const Block = styled.div``

const SideBar = styled.div`
  padding-left: 32px;
  height: 100%;
  position: relative;
`

/** Страница карточки профессии */
const ProfIdPage: FC<Pick<IProfIdPageProps, 'availableProgramTypes'>> = (props) => {
  const { availableProgramTypes } = props

  const profession = useSelector(professionsIdSelectors.entity)

  const professionSimilar = useSelector(professionsIdSelectors.similar)
  const institutions = useSelector(institutionsSelectors.selectAll)
  const specialities = useSelector(specialitiesSearchSelectors.selectAll)

  const {
    stats,
    description,
    whyIsInDemand,
    howToStudy,
    shortDescription,
    skills,
    career,
    companies,
    cases,
    popularPersons,
    tagIds,
    specialityIds,
  } = profession || {}

  const { convertCaseName, title, programTypes, params } = useProfByIdCore(
    profession,
    availableProgramTypes
  )

  const namePlural = convertCaseName(profession?.namePlural)
  const nameDative = convertCaseName(profession?.nameDative)
  const nameGenitive = convertCaseName(profession?.nameGenitive)
  const name = convertCaseName(profession?.name)

  if (!profession) return null

  return (
    <Block>
      <Container>
        <Spacing spacing='xlarge' />

        <Grid spacing='xlarge'>
          <GridCol uxs={18}>
            {/* Заголовок страницы */}
            {title && <Heading h1={title} spacing='xxsmall' />}

            {/* Статистика */}
            {stats && (
              <>
                <ProfCardStats
                  institutionsCount={stats.universitiesCount}
                  collagesCount={stats.collegesCount}
                  coursesCount={stats.coursesCount}
                  specialitiesCount={stats.specialitiesCount}
                  tagIds={tagIds}
                  specialityIds={specialityIds}
                />

                <Spacing spacing='small' />
              </>
            )}

            {/* Описание */}
            {shortDescription && (
              <>
                <Text>{shortDescription}</Text>

                <Spacing spacing='xlarge' />
              </>
            )}

            {/* Чем занимается {профессия} - description */}
            {description && (
              <>
                <TextBlock title={`Чем занимается ${name}`} description={description} />

                <Spacing spacing='xlarge' />
              </>
            )}

            {/* Что должен уметь {профессия} - skills */}
            {skills && skills.length > 0 && (
              <>
                <TextBlock
                  title={`Что должен уметь ${name}`}
                  description={skills.map((txt) => `•&nbsp;&nbsp;&nbsp;${txt}`)}
                />

                <Spacing spacing='xlarge' />
              </>
            )}

            {/* Почему востребована {профессия} - whyIsInDemand */}
            {whyIsInDemand && (
              <>
                <Spacing spacing='xlarge' />

                <TextBlock
                  title={`Почему востребована профессия ${nameGenitive || ''}`}
                  description={whyIsInDemand}
                />

                <Spacing spacing='xlarge' />
              </>
            )}

            {/* Как стать {профессия} - howToStudy  */}
            {howToStudy && (
              <>
                <Spacing spacing='xlarge' />

                <TextBlock
                  title={`Как стать ${nameDative || name || ''}`}
                  description={howToStudy}
                />

                <Spacing spacing='xlarge' />
              </>
            )}

            <Banner id={2} extraParams={params} />

            {/* Где учиться на {профессия} */}
            {institutions && institutions.length > 0 && (
              <>
                <Spacing spacing='uxlarge' />

                <WhereAreStudying
                  title={`Где учиться ${nameGenitive ? `на ${nameGenitive}` : ''}`}
                  items={institutions}
                  programTypes={programTypes}
                  tagsId={tagIds}
                  specialityIds={specialityIds}
                />

                <Spacing spacing='xlarge' />
              </>
            )}

            <Banner id={2} extraParams={params} />

            {/* Подходящие специальности */}
            {specialities && specialities.length > 0 && (
              <>
                <Spacing spacing='uxlarge' />

                <SpecialitiesByProf
                  items={specialities}
                  programTypes={programTypes}
                  specialityIds={specialityIds}
                />

                <Spacing spacing='uxlarge' />
              </>
            )}

            {/* Карьера {профессия} - career */}
            {(career || (companies && companies.length > 0)) && (
              <>
                <Spacing spacing='xlarge' />

                <Heading h2={`Карьера ${nameGenitive || ''}`} spacing='large' />

                <TextBlock description={career} />

                <Spacing spacing='xlarge' />
              </>
            )}

            {/* Где работают {профессия} - companies */}
            {companies && companies.length > 0 && (
              <>
                <Spacing spacing='xlarge' />

                <AboutCompanies
                  title={`Где работают ${namePlural || ''}`}
                  items={companies}
                />

                <Spacing spacing='xlarge' />
              </>
            )}

            {/* Интересные кейсы - cases */}
            {cases && cases.length > 0 && (
              <>
                <Spacing spacing='uxlarge' />

                <AboutCases items={cases} />

                <Spacing spacing='uxlarge' />
              </>
            )}

            {/* Известные {профессия} - popularPersons  */}
            {popularPersons && popularPersons.length > 0 && (
              <>
                <Spacing spacing='uxlarge' />

                <PopularPersons
                  title={`Известные ${namePlural || 'личности'}`}
                  items={popularPersons}
                />

                <Spacing spacing='uxlarge' />
              </>
            )}

            {/* Похожие профессии  */}
            {professionSimilar?.items && professionSimilar?.items.length > 0 && (
              <>
                <Spacing spacing='uxlarge' />

                <SimilarProfBlock items={professionSimilar?.items} />
              </>
            )}

            <Spacing spacing='uxlarge' />
          </GridCol>

          <GridCol uxs={6}>
            <SideBar>
              <div style={{ position: 'sticky', top: '16px' }}>
                <Banner id={3} extraParams={params} />
              </div>
            </SideBar>
          </GridCol>
        </Grid>
      </Container>

      <Banner id={6} extraParams={params} />
      <Banner id={14} extraParams={params} />
    </Block>
  )
}

export default ProfIdPage
